/**
 * Korean handicap (점) → AI skill profile.
 *
 * In Korean 4구 a player's "점수" is at once their handicap (the points they must
 * clear) AND their rank. We therefore drive the computer opponent straight off
 * the chosen level, mapping that one number to:
 *
 *   (a) a target AVERAGE — points per inning (애버리지) — fit to the competitive
 *       bands below, and
 *   (b) the shot REPERTOIRE and execution accuracy that visibly produce that
 *       style of play.
 *
 * Documented bands this is fit to (points cleared / innings taken):
 *   80~100점 : 30~40 innings   → avg ≈ 2.0–3.3
 *   150~200점: 20~25 innings   → avg ≈ 6–10
 *   250~300점: 15~18 innings   → avg ≈ 14–20
 *   400점+   : within 12 innings → avg ≈ 33+
 *
 * Style by tier (what the search is allowed to attempt — see ShotSearch):
 *   ≤80   쌩초보  — direct angle + thin cuts with a little push(오시)/draw(히끼);
 *                   centre-ball only, no side English, no massé.
 *   100~150       — basic 4구 patterns, gathers a little (모아치기); push/draw mix
 *                   plus light side English.
 *   200~250       — actively gathers (가락) and, with a deeper search, reads basic
 *                   systems (뒤돌려치기 / banks); fuller side English.
 *   300+          — natural 3-cushion transitions, free english (시내루) and gentle
 *                   massé; strong gather keeps the run alive.
 */

export interface SkillProfile {
  /** The handicap/level this profile was built for. */
  level: number;
  /** Points-per-inning this rank should average over a rack. */
  targetAverage: number;
  /** Per-shot make probability that yields targetAverage (A / (1 + A)). */
  makeProbability: number;
  /**
   * Measured fraction of random layouts where searchBest finds a make at all,
   * at this anchor's own samples/refineRounds — see findrate.test.ts. This is
   * a hard ceiling on the realized average no matter how the governor is
   * tuned (a shot that's never found can't be committed to), which is why
   * commitProbability below compensates for it instead of makeProbability
   * being used directly as the governor's commit chance.
   */
  foundRate: number;
  /**
   * The governor's actual commit chance: makeProbability boosted by how often
   * a make is found at all, capped short of 1 so a miss stays possible. Without
   * this, realized averages land far under target at every tier — found-rate
   * is a much bigger bottleneck than commit-probability tuning alone can fix
   * (see docs/AI.md "Known gaps" #2 and the calibration harness).
   */
  commitProbability: number;

  // ---- search effort ----
  /** Broad random candidate shots sampled before refinement. */
  samples: number;
  /** Local hill-climb rounds around the best sample (finds banks/systems). */
  refineRounds: number;
  /**
   * Position play: how many of the best makes to look one shot ahead on, picking
   * the carom that leaves the NEXT carom makeable (모아치기/가락). 0 = no foresight
   * (beginners take the bare point); grows with rank.
   */
  lookaheadK: number;

  // ---- execution (the make-rate governor reads these) ----
  /** Aim scatter (rad) when COMMITTING to a found make — tight, still scores. */
  hitNoise: number;
  /** Aim scatter (rad) of a deliberate shaky-stroke miss. */
  missNoise: number;
  /** Speed scatter (m/s) applied to the committed shot. */
  speedNoise: number;
  /** Tip-offset scatter applied to the committed shot. */
  spinNoise: number;

  // ---- repertoire caps (bound the search space → the tier's "look") ----
  minSpeed: number; // m/s
  maxSpeed: number; // m/s — beginners don't blast
  sideSpin: number; // |a| side-English cap (0 = centre-ball only)
  topDraw: number; // |b| push/draw cap
  maxElevation: number; // massé cap (rad); 0 = flat cue only
  /** Reward for leaving the balls gathered for the next shot (모아치기/가락). */
  gatherWeight: number;

  // ---- derived tier traits (computed, not anchored) ----
  /** Net-path candidate budget (server propose-and-verify). */
  netSamples: number;
  /** Net-path verified-candidate budget (top-K real sims). */
  verifyK: number;
  /** Candidates net-scored from each make's leave (0 = no position look-ahead). */
  leaveSamples: number;
  /** Leave look-ahead depth on the net path (0/1/2 ply). */
  leavePly: number;
  /** Preference for the naive first-instinct line (straight at the nearest ball). */
  naiveBias: number;
  /** Speed-judgement coarseness (m/s grid the tier can distinguish; 0 = exact). */
  speedQuant: number;
}

/** Profile anchors by level; everything in between is interpolated. */
interface Anchor
  extends Omit<
    SkillProfile,
    | 'level'
    | 'makeProbability'
    | 'commitProbability'
    | 'netSamples'
    | 'verifyK'
    | 'leaveSamples'
    | 'leavePly'
    | 'naiveBias'
    | 'speedQuant'
  > {
  level: number;
}

// Tuned against the documented innings bands (see ai/__tests__/calibration).
// Monotonic in level on every axis: more samples + refinement, tighter stroke,
// wider repertoire, and stronger gathering as the rank climbs.
// foundRate = measured searchBest find-rate at this anchor (findrate.test.ts,
// CALIBRATE=1) — the ceiling commitProbability compensates for. Re-measure
// after changing samples/refineRounds; a stale value under-corrects silently.
const ANCHORS: Anchor[] = [
  // level  tgtAvg  found  samp  refine look hit    miss  spd   spin  minSp maxSp side  draw  elev  gather
  { level: 30,  targetAverage: 1.0,  foundRate: 0.50,  samples: 100, refineRounds: 1, lookaheadK: 0, hitNoise: 0.0060, missNoise: 0.13, speedNoise: 0.9,  spinNoise: 0.00, minSpeed: 1.2, maxSpeed: 3.0, sideSpin: 0.00, topDraw: 0.15, maxElevation: 0,    gatherWeight: 0.0 },
  { level: 80,  targetAverage: 2.4,  foundRate: 0.72,  samples: 260, refineRounds: 2, lookaheadK: 0, hitNoise: 0.0050, missNoise: 0.11, speedNoise: 0.8,  spinNoise: 0.03, minSpeed: 1.2, maxSpeed: 3.3, sideSpin: 0.00, topDraw: 0.25, maxElevation: 0,    gatherWeight: 0.6 },
  { level: 120, targetAverage: 4.5,  foundRate: 0.80,  samples: 300, refineRounds: 3, lookaheadK: 2, hitNoise: 0.0040, missNoise: 0.09, speedNoise: 0.6,  spinNoise: 0.06, minSpeed: 1.0, maxSpeed: 3.7, sideSpin: 0.14, topDraw: 0.30, maxElevation: 0,    gatherWeight: 2.2 },
  { level: 175, targetAverage: 7.8,  foundRate: 0.883, samples: 210, refineRounds: 2, lookaheadK: 4, hitNoise: 0.0034, missNoise: 0.08, speedNoise: 0.45, spinNoise: 0.10, minSpeed: 1.0, maxSpeed: 4.1, sideSpin: 0.22, topDraw: 0.35, maxElevation: 0,    gatherWeight: 3.8 },
  { level: 250, targetAverage: 14.0, foundRate: 0.917, samples: 340, refineRounds: 3, lookaheadK: 6, hitNoise: 0.0028, missNoise: 0.07, speedNoise: 0.33, spinNoise: 0.16, minSpeed: 0.9, maxSpeed: 4.5, sideSpin: 0.32, topDraw: 0.42, maxElevation: 0.04, gatherWeight: 5.4 },
  { level: 300, targetAverage: 17.5, foundRate: 1.00,  samples: 430, refineRounds: 3, lookaheadK: 8, hitNoise: 0.0024, missNoise: 0.07, speedNoise: 0.27, spinNoise: 0.20, minSpeed: 0.9, maxSpeed: 4.7, sideSpin: 0.38, topDraw: 0.46, maxElevation: 0.06, gatherWeight: 6.4 },
  { level: 400, targetAverage: 33.0, foundRate: 0.983, samples: 600, refineRounds: 4, lookaheadK: 10, hitNoise: 0.0020, missNoise: 0.06, speedNoise: 0.20, spinNoise: 0.26, minSpeed: 0.8, maxSpeed: 5.0, sideSpin: 0.46, topDraw: 0.50, maxElevation: 0.09, gatherWeight: 7.8 },
  { level: 500, targetAverage: 45.0, foundRate: 0.98,  samples: 760, refineRounds: 5, lookaheadK: 12, hitNoise: 0.0016, missNoise: 0.05, speedNoise: 0.16, spinNoise: 0.30, minSpeed: 0.8, maxSpeed: 5.2, sideSpin: 0.50, topDraw: 0.50, maxElevation: 0.11, gatherWeight: 8.8 },
];

const lerp = (a: number, b: number, s: number) => a + (b - a) * s;

/** Resolve a continuous skill profile for any handicap/level. */
export function skillForLevel(level: number): SkillProfile {
  const lvl = Math.max(1, level);
  // find the bracketing anchors
  let lo = ANCHORS[0];
  let hi = ANCHORS[ANCHORS.length - 1];
  if (lvl <= lo.level) hi = lo;
  else if (lvl >= hi.level) lo = hi;
  else {
    for (let i = 0; i < ANCHORS.length - 1; i++) {
      if (lvl >= ANCHORS[i].level && lvl <= ANCHORS[i + 1].level) {
        lo = ANCHORS[i];
        hi = ANCHORS[i + 1];
        break;
      }
    }
  }
  const s = hi.level === lo.level ? 0 : (lvl - lo.level) / (hi.level - lo.level);

  const mix = (k: Exclude<keyof Anchor, 'level'>) => lerp(lo[k], hi[k], s);
  const targetAverage = mix('targetAverage');
  const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

  // Run length is geometric in the make rate p: E[points/inning] = p / (1 - p),
  // so the make rate that yields the target average is A / (1 + A).
  const makeProbability = targetAverage / (1 + targetAverage);
  // The governor only gets a vote when a make is FOUND at all, so the realized
  // rate is foundRate * commitProbability, not commitProbability alone. Boost
  // the commit chance to compensate — capped short of 1 so a miss stays
  // possible even at a tier whose foundRate is already ~100%.
  const commitProbability = clamp(makeProbability / mix('foundRate'), 0, 0.97);

  return {
    level: lvl,
    targetAverage,
    foundRate: mix('foundRate'),
    makeProbability,
    commitProbability,
    // Derived tier traits — MUST stay in sync with server/skill.mjs.
    netSamples: clamp(Math.round(220 + lvl), 250, 720),
    verifyK: clamp(Math.round(8 + lvl * 0.06), 8, 40),
    leaveSamples: lvl >= 100 ? Math.min(160, Math.round(48 + lvl * 0.25)) : 0,
    leavePly: lvl >= 300 ? 2 : lvl >= 100 ? 1 : 0,
    naiveBias: clamp((160 - lvl) / 160, 0, 1),
    speedQuant: Math.max(0, (140 - lvl) / 140) * 0.9,
    samples: Math.round(mix('samples')),
    refineRounds: Math.round(mix('refineRounds')),
    lookaheadK: Math.round(mix('lookaheadK')),
    hitNoise: mix('hitNoise'),
    missNoise: mix('missNoise'),
    speedNoise: mix('speedNoise'),
    spinNoise: mix('spinNoise'),
    minSpeed: mix('minSpeed'),
    maxSpeed: mix('maxSpeed'),
    sideSpin: mix('sideSpin'),
    topDraw: mix('topDraw'),
    maxElevation: mix('maxElevation'),
    gatherWeight: mix('gatherWeight'),
  };
}
